import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { SERVICES_COMPTABLES } from '@artosera/shared';
import { Badge } from '@artosera/ui';
import { listerGaleries } from '../../lib/api.js';
import { pourcentageEnSaisie } from '../../lib/montant.js';
import type { RevenuSaisie } from '../../types.js';
import { CelluleMontant, EtatOnglet, formaterMois, useLangue, useMessageErreur } from './commun.js';
import { TableauSaisie, type ChampSaisie } from './TableauSaisie.js';
import { useSaisies } from './useSaisies.js';

/**
 * Les revenus ponctuels (exploitation, direction) : ce qui n'est pas
 * récurrent. La catégorie est un texte libre — la ventilation évolue sans
 * migration — et sort telle quelle dans la vue des associés.
 */
export function RevenusTab({ enEcriture }: { enEcriture: boolean }): JSX.Element {
  const { t } = useTranslation('console');
  const langue = useLangue();
  const message = useMessageErreur('revenus');
  const { lignes, chargement, enErreur, creer, modifier, supprimer } = useSaisies('revenus');
  // Le parc ne bloque pas l'onglet : sans lui, seul le rattachement manque.
  const galeries = useQuery({ queryKey: ['console', 'galeries'], queryFn: listerGaleries });
  const parc = galeries.data ?? [];

  const champs: ChampSaisie[] = [
    { cle: 'mois', titre: t('finances.revenus.champs.mois'), type: 'mois', requis: true },
    {
      cle: 'categorie',
      titre: t('finances.revenus.champs.categorie'),
      type: 'texte',
      requis: true,
      longueurMax: 60,
      aide: t('saisies.visibleAssocies'),
    },
    {
      cle: 'libelle',
      titre: t('finances.revenus.champs.libelle'),
      type: 'texte',
      requis: true,
      longueurMax: 160,
    },
    // Le service décide de la répartition ; vide, le revenu reste « à classer ».
    {
      cle: 'service',
      titre: t('finances.revenus.champs.service'),
      type: 'choix',
      optionVide: t('finances.revenus.aClasser'),
      aide: t('finances.revenus.serviceAide'),
      options: SERVICES_COMPTABLES.map((service) => ({
        value: service,
        label: t(`comptabilite.services.${service}`),
      })),
    },
    // Facultative : la galerie de la recette, que suivent les galeries exclues.
    {
      cle: 'galerieId',
      titre: t('finances.revenus.champs.galerie'),
      type: 'choix',
      optionVide: t('finances.revenus.sansGalerie'),
      aide: t('finances.revenus.galerieAide'),
      options: parc
        .filter((galerie) => !galerie.estTest)
        .map((galerie) => ({ value: galerie.id, label: galerie.nom })),
    },
    { cle: 'montantCentimes', titre: t('finances.revenus.champs.montant'), type: 'montant' },
    // 20 % au départ, modifiable : 0 % est une valeur permise, pas un oubli.
    {
      cle: 'tauxTvaPointsDeBase',
      titre: t('finances.revenus.champs.tva'),
      type: 'pourcentage',
      requis: true,
      initiale: '20',
      aide: t('finances.revenus.tvaAide'),
    },
  ];

  if (chargement || enErreur) return <EtatOnglet chargement={chargement} enErreur={enErreur} />;
  return (
    <TableauSaisie<RevenuSaisie>
      legende={t('finances.onglets.revenus')}
      introduction={t('finances.revenus.texte')}
      lignes={lignes}
      colonnes={[
        {
          cle: 'mois',
          titre: t('finances.revenus.colonnes.mois'),
          rendu: (ligne) => formaterMois(ligne.mois, langue),
        },
        {
          cle: 'service',
          titre: t('finances.revenus.colonnes.service'),
          rendu: (ligne) =>
            ligne.service ? (
              t(`comptabilite.services.${ligne.service}`)
            ) : (
              <Badge tone="reserved">{t('finances.revenus.aClasser')}</Badge>
            ),
        },
        {
          cle: 'categorie',
          titre: t('finances.revenus.colonnes.categorie'),
          rendu: (ligne) => ligne.categorie,
        },
        {
          cle: 'libelle',
          titre: t('finances.revenus.colonnes.libelle'),
          rendu: (ligne) => ligne.libelle,
        },
        {
          cle: 'montant',
          titre: t('finances.revenus.colonnes.montant'),
          numerique: true,
          rendu: (ligne) => <CelluleMontant centimes={ligne.montantCentimes} />,
        },
        {
          cle: 'tva',
          titre: t('finances.revenus.colonnes.tva'),
          numerique: true,
          rendu: (ligne) => `${pourcentageEnSaisie(ligne.tauxTvaPointsDeBase)} %`,
        },
      ]}
      champs={champs}
      valeursDe={(ligne) => ({
        mois: ligne.mois,
        categorie: ligne.categorie,
        libelle: ligne.libelle,
        service: ligne.service,
        galerieId: ligne.galerieId,
        montantCentimes: ligne.montantCentimes,
        tauxTvaPointsDeBase: ligne.tauxTvaPointsDeBase,
      })}
      libelleLigne={(ligne) => `${ligne.categorie} · ${ligne.libelle}`}
      enEcriture={enEcriture}
      libelleAjouter={t('finances.revenus.ajouter')}
      messageVide={t('finances.revenus.vide')}
      sansMontant={lignes.filter((ligne) => ligne.montantCentimes === null).length}
      surCreer={creer}
      surModifier={(ligne, changements) => modifier(ligne.id, changements)}
      surSupprimer={(ligne) => supprimer(ligne.id)}
      message={message}
    />
  );
}
